import { useEffect, useRef, useState } from 'react'
import { useModal } from '../../context/ModalContext'
import { GuidePicker } from '../guides/GuidePicker'

function ChatComposer({ disabled, onSend, onTyping }) {
  const { openModal } = useModal()
  const [text, setText] = useState('')
  const [items, setItems] = useState([])
  const [guides, setGuides] = useState([])
  const ref = useRef(null)
  const typing = useRef(false)
  const lastEmit = useRef(0)
  const timer = useRef(null)

  const stopTyping = () => {
    clearTimeout(timer.current)
    if (!typing.current) return
    typing.current = false
    onTyping?.(false)
  }

  const noteTyping = (value) => {
    if (!value.trim()) {
      stopTyping()
      return
    }
    const now = Date.now()
    if (!typing.current || now - lastEmit.current > 2000) {
      typing.current = true
      lastEmit.current = now
      onTyping?.(true)
    }
    clearTimeout(timer.current)
    timer.current = setTimeout(stopTyping, 3000)
  }

  // The timer and typing flag live in refs; this only fires the stop on unmount.
  useEffect(() => {
    return () => {
      clearTimeout(timer.current)
      if (typing.current) onTyping?.(false)
    }
  }, [onTyping])

  const resize = () => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`
  }

  const send = () => {
    const trimmed = text.trim()
    if (disabled) return
    if (!trimmed && !items.length && !guides.length) return
    stopTyping()
    onSend?.({ text: trimmed, items, guides })
    setText('')
    setItems([])
    setGuides([])
    requestAnimationFrame(resize)
  }

  const pickItems = () => {
    openModal('chat-attach', {
      items,
      guides,
      onSave: (next) => {
        setItems(next.items || [])
        setGuides(next.guides || [])
      },
    })
  }

  const attached = items.length + guides.length

  return (
    <div className="crm-composer">
      {guides.length ? (
        <div className="crm-composer-guides">
          <GuidePicker value={guides} onChange={setGuides} label="Guides" />
        </div>
      ) : null}
      <div className="crm-composer-box">
        <textarea
          ref={ref}
          className="crm-composer-input"
          rows={1}
          value={text}
          placeholder="Message"
          disabled={disabled}
          onChange={(event) => {
            setText(event.target.value)
            noteTyping(event.target.value)
            resize()
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              send()
            }
          }}
        />
        <button type="button" className="crm-icon-btn" onClick={pickItems} disabled={disabled} title="Attach">
          Attach{attached ? ` (${attached})` : ''}
        </button>
        <button type="button" className="crm-send" onClick={send} disabled={disabled}>
          Send
        </button>
      </div>
    </div>
  )
}

export default ChatComposer
